import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { PageContainer } from '@/components/landing/sections';
import { LEGAL_REVISED_ON } from './business-info';

/**
 * 法務ページ (利用規約・プライバシーポリシー・特商法表記) の外枠。
 *
 * 3 ページで違うのは本文だけなので、ヘッダー・見出し・フッターはここに置く。
 * ログイン状態を見ないのは、この 3 ページが未ログインでも必ず開ける必要が
 * あるため (フッターから辿れないと規約を読めないまま契約させることになる)。
 */
export function LegalLayout({
  title,
  lead,
  children,
}: {
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-surface text-ink">
      <header className="border-b border-line bg-surface-raised">
        <PageContainer className="flex items-center justify-between gap-3 py-3.5 sm:py-5">
          <Link to="/" aria-label="INOU トップへ">
            {/* 公開ページのヘッダーは LP と同じ寸法 (狭い幅で 36、640px 以上で 48)。並べて見たときに揃う */}
            <Logo height={48} className="h-9 w-auto text-brand-500 sm:h-12" />
          </Link>
          <Link to="/" className="text-sm text-ink-muted transition-colors hover:text-ink">
            トップへ戻る
          </Link>
        </PageContainer>
      </header>

      <main className="mx-auto max-w-3xl px-5 pt-10 pb-14 sm:px-8 sm:pt-16 sm:pb-20 md:px-14">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {lead ? <p className="mt-5 text-sm leading-relaxed text-ink-muted">{lead}</p> : null}
        <p className="mt-5 font-mono text-xs text-ink-subtle">最終改定日: {LEGAL_REVISED_ON}</p>

        <div className="mt-9 flex flex-col gap-8 sm:mt-12 sm:gap-10">{children}</div>
      </main>

      <SiteFooter />
    </div>
  );
}

/** 条・章。見出しと本文をまとめる */
export function Article({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-t border-line pt-6 sm:pt-8">
      <h2 className="text-base font-bold">{heading}</h2>
      <div className="flex flex-col gap-3 text-sm leading-relaxed text-ink-muted">{children}</div>
    </section>
  );
}

/** 条の中の箇条書き */
export function List({ items }: { items: ReactNode[] }) {
  return (
    <ol className="flex flex-col gap-2 pl-1">
      {items.map((item, index) => (
        // 項番は表示順そのものなので index を key にしてよい
        // biome-ignore lint/suspicious/noArrayIndexKey: 上のとおり項番 = 表示順
        <li key={index} className="flex gap-3">
          <span aria-hidden className="font-mono text-xs text-ink-faint">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="flex-1">{item}</span>
        </li>
      ))}
    </ol>
  );
}

/** 特商法表記のような「項目 / 内容」の対応表 */
export function DefinitionTable({ rows }: { rows: { term: string; value: ReactNode }[] }) {
  return (
    <dl className="border-t border-line">
      {rows.map((row) => (
        <div
          key={row.term}
          className="grid gap-2 border-b border-line py-4 sm:grid-cols-[13rem_1fr] sm:gap-6 sm:py-5"
        >
          <dt className="text-sm font-bold">{row.term}</dt>
          <dd className="text-sm leading-relaxed text-ink-muted">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
